import React from 'react';
import AppDetailsContainer from '../../../components/AppDetailsContainer';
import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import MainPreview from '../MainPreview';
import { StepName } from '../MainPreview/types';
import IntlInputComponent from '../../../components/WaardecheckTemplate/components/IntlInputComponent';
import { biedwijzerContentState } from '../..';
import { useRecoilState } from 'recoil';
import { mergeDeepRight } from 'ramda';
import Divider from '~/components/atom/Divider';
import Input from '~/components/molecule/Input';
import MultiValueInput from '~/components/molecule/MultiValueInput';
import type { AppBbWaardecheckText, UnionImage } from '~/graphql/types';
import { getDefaultValues } from '../../appConfig';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { type ValidationFunction } from '~/util/getValidationErrors';
import { Body } from '~/components/atom/Typography';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import AppEntryItemHeader from '../../../components/AppEntryItemHeader';
import styled from 'styled-components';
import Div from '~/components/atom/Div';

const text = {
  header: 'Instellingen',
  officeNumbers: 'NVM Vestigingsnummer',
  officeNumbersLabel: 'Vestigingsnummers',
  addOfficeNumber: 'Vestigingsnummer toevoegen',
  minOneNumber: 'Er moet minimaal één vestigingsnummer aanwezig zijn',
  invalidNumber: 'Voer een geldig nummer in',
  officeNumberExplanation:
    'Om belangenverstrengeling te voorkomen, kun je hier de NVM-vestigingsnummers van jouw kantoor toevoegen. De BiedWijzer wordt dan automatisch uitgeschakeld voor woningen die je eigen kantoor in verkoop heeft.',
  tracking: 'Tracking',
  trackingExplanation:
    'Het gedrag van bezoekers wordt bijgehouden en kan worden gebruikt voor het bijhouden van de conversie of het optimaliseren van advertentiecampagnes. In de kennisbank vind je de gebruikshandleiding voor Google en Facebook.',
  googleAnalytics4: 'Google Analytics 4',
  facebookPixel: 'Facebook Pixel',
  privacyStatementLabel: 'Disclaimer',
};

export type Props = RouteComponentProps & {};

// Validation functions
const isNumeric: ValidationFunction = (value: string) => {
  if (!/^\d+$/.test(value)) {
    return text.invalidNumber;
  }
  return true;
};

const Settings: React.FCC<Props> = () => {
  const [values, setValues] = useRecoilState(biedwijzerContentState);

  const onUpdate = (
    fieldName: string,
    value: Partial<AppBbWaardecheckText> | string | UnionImage | Array<string>,
  ) => {
    const isString = typeof value === 'string';
    const isArray = Array.isArray(value);

    setValues(prev => {
      if (!prev) return prev;

      if (fieldName === 'officeNumbers') {
        return {
          ...prev,
          [fieldName]: value as Array<string>,
        };
      }

      return {
        ...prev,
        content: prev.content
          ? {
              ...prev.content,
              general: {
                ...prev.content.general,
                [fieldName]:
                  isString || isArray
                    ? value
                    : mergeDeepRight(prev.content.general[fieldName], value),
              },
            }
          : prev.content,
      };
    });
  };

  // Office numbers management
  const officeNumbers = values?.officeNumbers || [];

  const addOfficeNumber = (value: string) => {
    if (value && !officeNumbers.includes(value)) {
      onUpdate('officeNumbers', [...officeNumbers, value]);
    }
  };

  const removeOfficeNumber = (value: string) => {
    const newNumbers = officeNumbers.filter(num => num !== value);

    // Prevent removing the last number
    if (newNumbers.length > 0) {
      onUpdate('officeNumbers', newNumbers);
    }
  };

  const accountName = useCurrentAccount().name ?? '';

  return (
    <AppDetailsContainer header={text.header} icon="gear">
      <MainPreview
        values={values?.content}
        data={{
          appConfigData: {
            general: {
              privacyStatement: {
                nl: values?.content?.general.privacyStatement?.nl || '',
                en: values?.content?.general.privacyStatement?.en || '',
              },
              googleAnalytics4: values?.content?.general.googleAnalytics4 || '',
              facebookPixel: values?.content?.general.facebookPixel || '',
            },
          },
        }}
        step={StepName.BasicContactForm}
      />

      <IntlInputComponent
        title={text.privacyStatementLabel}
        value={{
          nl: values?.content?.general.privacyStatement?.nl,
          en: values?.content?.general.privacyStatement?.en,
        }}
        onChange={privacyStatement => {
          onUpdate('privacyStatement', privacyStatement);
        }}
        defaultValue={{
          nl: getDefaultValues(accountName).general.privacyStatement.nl,
          en: getDefaultValues(accountName).general.privacyStatement.en,
        }}
      />

      <Divider />

      <JustificationContainer direction="column" width="100%" gap="base">
        <StyledAppEntryItemHeader required>
          {text.officeNumbers}
        </StyledAppEntryItemHeader>
        <Body>{text.officeNumberExplanation}</Body>
        <Div width="100%">
          <MultiValueInput
            label={text.officeNumbersLabel}
            selectedValues={officeNumbers}
            onAdded={addOfficeNumber}
            onRemoved={removeOfficeNumber}
            validation={[isNumeric]}
            addButton={{
              label: text.addOfficeNumber,
              icon: 'plus',
            }}
          />
        </Div>
      </JustificationContainer>

      <Divider />
      <JustificationContainer width="100%" direction="column" gap="base">
        <StyledAppEntryItemHeader>{text.tracking}</StyledAppEntryItemHeader>
        <Body>{text.trackingExplanation}</Body>
        <Input
          label={{ text: text.googleAnalytics4 }}
          value={values?.content?.general.googleAnalytics4}
          onChange={e => {
            onUpdate('googleAnalytics4', e.target.value);
          }}
          width="100%"
          placeholder="G-..."
        />

        <Input
          label={{ text: text.facebookPixel }}
          value={values?.content?.general.facebookPixel}
          onChange={e => {
            onUpdate('facebookPixel', e.target.value);
          }}
          width="100%"
          placeholder="1234..."
        />
      </JustificationContainer>
    </AppDetailsContainer>
  );
};

const StyledAppEntryItemHeader = styled(AppEntryItemHeader)`
  margin: 0;
`;

export default Settings;
